import { Close, KeyboardArrowDown } from '@mui/icons-material';
import { MenuItem, Select, SvgIcon, Checkbox, FormControlLabel } from '@mui/material';
import React, { useState } from 'react';
import moment from 'moment';
import { useAccountStore } from '../../../components/Stores/accountStore';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import ButtonDesigned from '../../../components/UI-Elements/Buttons/ButtonDesigned';
import { getCurrencySymbol, useCurrencyRates } from '../../../hooks/useCurrencyRates';
import { getUnitObj } from '../../../utils/MassUnits/massUnitsUtils';
import { LoadingSpinnerOverlay } from '../../../components/UI-Elements/LoadingSpinnerOverlay';
import { IFutureOrder } from '../../../sp-types/FutureOrderType';
import { Timestamp } from 'firebase/firestore';
import { createFutureOrder, saveExistingFutureOrder } from '../../../sp-firebase/futureOrderFB';
import { MonthYearValue } from '../../../utils/MonthYear/MonthYearValue';
import * as Sentry from '@sentry/react';

const BlueArrowIcon = (props: any) => (
  <SvgIcon {...props} style={{ color: '#6571ff' }}>
    <KeyboardArrowDown />
  </SvgIcon>
);

interface MonthData {
  month: number;
  year: number;
  value: number;
}

interface Props {
  cancel: () => void;
  onCommitmentAdded: () => void;
  isUpdate?: boolean;
  updateRow?: IFutureOrder;
}

export const AddSalesCommitmentPopup: React.FC<Props> = ({
  cancel,
  onCommitmentAdded,
  isUpdate = false,
  updateRow,
}) => {
  const { materialsMap } = useMaterialsStore();
  const { account } = useAccountStore();
  const { getCurrencyInUSD } = useCurrencyRates();
  const [selectedMaterial, setSelectedMaterial] = useState<string>(updateRow?.supplierExposure?.[0]?.materialId || '');
  const [clientName, setClientName] = useState<string>(updateRow?.clientName || '');
  const [loading, setLoading] = useState<boolean>(false);
  const [showAllMonths, setShowAllMonths] = useState<boolean>(false);
  const [actualPrice, setActualPrice] = useState<number>(updateRow?.orderDetails?.pricePerUnit || 0);
  const [dontSum, setDontSum] = useState<boolean>(updateRow?.dontSum || false);

  // Initialize months data - always create 24 months
  const initializeMonths = (): MonthData[] => {
    const startDate = moment();
    const months: MonthData[] = [];

    if (isUpdate && updateRow?.supplierExposure?.[0]?.exposureCalendar) {
      // Load existing data
      const calendar = updateRow.supplierExposure[0].exposureCalendar;

      // Find the earliest month in the calendar to use as starting point
      const sortedCalendar = [...calendar].sort((a, b) => {
        if (a.date.year !== b.date.year) return a.date.year - b.date.year;
        return a.date.month - b.date.month;
      });

      const firstEntry = sortedCalendar[0];
      const startMonth = moment(`${firstEntry.date.year}-${firstEntry.date.month}`, 'YYYY-M');

      // Create a map of existing values for quick lookup
      const existingValuesMap = new Map<string, number>();
      calendar.forEach((item) => {
        const key = `${item.date.year}-${item.date.month}`;
        existingValuesMap.set(key, item.value);
      });

      // Create 24 months starting from the first month, filling in existing values
      for (let i = 0; i < 24; i++) {
        const currentDate = moment(startMonth).add(i, 'months');
        const monthNum = currentDate.month() + 1;
        const yearNum = currentDate.year();
        const key = `${yearNum}-${monthNum}`;

        months.push({
          month: monthNum,
          year: yearNum,
          value: existingValuesMap.get(key) || 0,
        });
      }

      // If loaded data has more than 12 months with values, show all by default
      if (calendar.length > 12) {
        setShowAllMonths(true);
      }

      return months;
    } else {
      // Create 24 months
      for (let i = 0; i < 24; i++) {
        const currentDate = moment(startDate).add(i, 'months');
        months.push({
          month: currentDate.month() + 1,
          year: currentDate.year(),
          value: 0,
        });
      }
    }
    return months;
  };

  const [monthsData, setMonthsData] = useState<MonthData[]>(initializeMonths());

  React.useEffect(() => {
    if (selectedMaterial && materialsMap[selectedMaterial]?.currentPrice) {
      // Use the material's currentPrice (in original currency), not pricesMap (which is converted to USD)
      setActualPrice(Math.floor(materialsMap[selectedMaterial].currentPrice));
    }
  }, [selectedMaterial, materialsMap]);

  const handleMonthValueChange = (index: number, value: number) => {
    const newMonthsData = [...monthsData];
    newMonthsData[index].value = value;
    setMonthsData(newMonthsData);
  };

  const getTotalAmount = (): number => {
    return monthsData.reduce((sum, month) => sum + month.value, 0);
  };

  const getMonthLabel = (month: number, year: number): string => {
    return moment(`${year}-${month}`, 'YYYY-M').format('MMM YY');
  };

  const saveSalesCommitment = async () => {
    if (!selectedMaterial || !clientName || getTotalAmount() === 0) {
      return;
    }

    setLoading(true);

    try {
      const exposureCalendar: MonthYearValue[] = monthsData
        .filter((m) => m.value > 0)
        .map((m) => ({
          date: { month: m.month, year: m.year },
          value: m.value,
        }));

      const monthExposure: Record<string, MonthYearValue[]> = {
        [selectedMaterial]: exposureCalendar,
      };

      // Calculate cumulative totalExposure for ALL months from current month forward
      // First, get the date range
      const now = moment();
      const currentMonth = now.month() + 1;
      const currentYear = now.year();

      // Create a map of monthly values for quick lookup
      const monthlyValuesMap = new Map<string, number>();
      exposureCalendar.forEach((item) => {
        const key = `${item.date.year}-${item.date.month}`;
        monthlyValuesMap.set(key, item.value);
      });

      // Find the last month with data
      const sortedExposure = [...exposureCalendar].sort((a, b) => {
        if (a.date.year !== b.date.year) return a.date.year - b.date.year;
        return a.date.month - b.date.month;
      });

      const lastDataMonth = sortedExposure[sortedExposure.length - 1];

      // Generate all months from current month to last data month
      const allMonths: { month: number; year: number }[] = [];
      const iterMonth = moment(`${currentYear}-${currentMonth}`, 'YYYY-M');
      const endMonth = moment(`${lastDataMonth.date.year}-${lastDataMonth.date.month}`, 'YYYY-M');

      while (iterMonth.isSameOrBefore(endMonth, 'month')) {
        allMonths.push({
          month: iterMonth.month() + 1,
          year: iterMonth.year(),
        });
        iterMonth.add(1, 'month');
      }

      // Calculate cumulative values: for each month, sum all values from that month forward
      const totalExposureCalendar: MonthYearValue[] = [];
      for (let i = 0; i < allMonths.length; i++) {
        const currentDate = allMonths[i];

        // Sum from current month to the end
        let cumulativeValue = 0;
        for (let j = i; j < allMonths.length; j++) {
          const futureDate = allMonths[j];
          const key = `${futureDate.year}-${futureDate.month}`;
          cumulativeValue += monthlyValuesMap.get(key) || 0;
        }

        totalExposureCalendar.push({
          date: currentDate,
          value: cumulativeValue,
        });
      }

      // Calculate total exposure sum for orderDetails.quantity
      const totalExposureSum = exposureCalendar.reduce((sum, { value }) => sum + value, 0);

      const futureOrder: Partial<IFutureOrder> = {
        uuid: updateRow?.uuid,
        orderId: updateRow?.orderId || `SC-${clientName}-${Date.now()}`,
        productId: selectedMaterial,
        fileUrl: [],
        clientName,
        futureOrderDate: Timestamp.now(),
        futureOrderType: 'BackToBack',
        orderDetails: {
          deliveryType: 'Sales Commitment',
          quantity: totalExposureSum,
          deliveryDates: exposureCalendar.map((e) => e.date),
          pricePerUnit: actualPrice,
          currency: materialsMap[selectedMaterial]?.currency || 'USD',
          productDeliveryCalendar: exposureCalendar,
        },
        clientExposure: {},
        supplierExposure: [
          {
            materialId: selectedMaterial,
            weightInProduct: 1,
            basePrice: 0,
            supplyLeadTime: 0,
            adjustment: 'none',
            exposureCalendar,
          },
        ],
        status: 'completed',
        updatedAt: Timestamp.now(),
        accountId: account.id,
        totalExposure: [{ materialId: selectedMaterial, exposureCalendar: totalExposureCalendar }],
        monthExposure,
        dontSum,
      };

      if (isUpdate && updateRow?.uuid) {
        await saveExistingFutureOrder(futureOrder as IFutureOrder);
      } else {
        await createFutureOrder(account.id || '', futureOrder);
      }

      onCommitmentAdded();
    } catch (error) {
      console.error('Error saving sales commitment:', error);
      Sentry.captureException(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative bg-white flex flex-col p-4 gap-4 w-full max-w-[800px] max-h-[90vh] overflow-y-auto">
      <div className="flex flex-row justify-between">
        <h4>{isUpdate ? 'Update Sales Commitment' : 'Add Sales Commitment'}</h4>
        <Close className="cursor-pointer align-self-baseline text-purple me-1" onClick={cancel} />
      </div>

      <div className="gap-2 flex flex-col">
        <p className="text-sm text-gray-450">Material</p>
        <Select
          className="w-full h-8 rounded-md"
          IconComponent={BlueArrowIcon}
          value={selectedMaterial}
          placeholder="Select material"
          onChange={({ target }) => setSelectedMaterial(target.value)}
          displayEmpty
          disabled={isUpdate}
        >
          <MenuItem value="" disabled>
            Select material
          </MenuItem>
          {account.materialsToAccount.map((item, index) => (
            <MenuItem key={index} id={item.materialId} value={item.materialId}>
              {materialsMap[item.materialId] && materialsMap[item.materialId].name}
            </MenuItem>
          ))}
        </Select>
      </div>

      <div className="gap-2 flex flex-col">
        <p className="text-sm text-gray-450">Client Name</p>
        <input
          title="Client Name"
          type="text"
          value={clientName}
          className="border-1 rounded-md border-gray-300 px-2 h-8 focus:outline-blue-600"
          onChange={({ target }) => setClientName(target.value)}
          placeholder="Enter client name"
        />
      </div>

      <div className="gap-2 flex flex-col">
        <FormControlLabel
          control={<Checkbox checked={dontSum} onChange={(e) => setDontSum(e.target.checked)} />}
          label="Don't include in dashboard calculations"
        />
      </div>

      <div className="gap-2 flex flex-col">
        <p className="text-sm text-gray-450">
          Price per{' '}
          {selectedMaterial && materialsMap[selectedMaterial]
            ? getUnitObj(materialsMap[selectedMaterial].unit).shortForm
            : 'unit'}
        </p>
        <div className="flex flex-row items-center gap-2">
          {selectedMaterial &&
            materialsMap[selectedMaterial]?.currency &&
            materialsMap[selectedMaterial].currency !== 'USD' && (
              <span className="text-lg font-medium text-gray-600">
                {getCurrencySymbol(materialsMap[selectedMaterial].currency)}
              </span>
            )}
          <input
            title="Actual Price"
            type="number"
            value={actualPrice || ''}
            disabled={!selectedMaterial}
            className="border-1 rounded-md border-gray-300 px-2 h-8 focus:outline-blue-600 w-32"
            onChange={({ target }) => setActualPrice(Number(target.value) || 0)}
            placeholder="0"
          />
        </div>
        {selectedMaterial &&
          materialsMap[selectedMaterial]?.currency &&
          materialsMap[selectedMaterial].currency !== 'USD' &&
          actualPrice > 0 && (
            <label className="text-sm text-gray-600">
              ≈ ${(actualPrice * getCurrencyInUSD(materialsMap[selectedMaterial].currency)).toFixed(2)} USD
            </label>
          )}
      </div>

      <div className="gap-2 flex flex-col">
        <p className="text-sm text-gray-450 font-semibold">Monthly Commitments</p>
        <div className="border rounded p-2">
          <div className="grid grid-cols-12 gap-2 max-h-96 overflow-y-auto">
            {monthsData.slice(0, showAllMonths ? 24 : 12).map((monthData, index) => (
              <div key={index} className="flex flex-col gap-1">
                <label className="text-xs text-gray-600 font-medium text-center">
                  {getMonthLabel(monthData.month, monthData.year)}
                </label>
                <input
                  title={`Value for ${getMonthLabel(monthData.month, monthData.year)}`}
                  type="number"
                  value={monthData.value || ''}
                  className="border-1 rounded-md border-gray-300 px-1 py-1 text-sm w-full focus:outline-blue-600"
                  onChange={({ target }) => handleMonthValueChange(index, Number(target.value) || 0)}
                  placeholder="0"
                />
              </div>
            ))}
          </div>
          {!showAllMonths && (
            <button
              type="button"
              onClick={() => setShowAllMonths(true)}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center justify-center gap-1 mt-2 w-full"
            >
              <KeyboardArrowDown style={{ fontSize: '16px' }} />
              <span>Add more</span>
              <KeyboardArrowDown style={{ fontSize: '16px' }} />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-row justify-between items-center p-3 bg-gray-100 rounded">
        <p className="text-sm text-gray-450 font-semibold">Total Amount:</p>
        <div className="flex gap-2 items-center">
          <label className="text-lg font-bold">{getTotalAmount().toFixed(2)}</label>
          <span className="text-sm text-gray-600">
            {selectedMaterial && materialsMap[selectedMaterial]
              ? getUnitObj(materialsMap[selectedMaterial].unit).shortForm
              : ''}
          </span>
        </div>
      </div>

      <div className="flex flex-row justify-between gap-4">
        <ButtonDesigned type="button" onButtonClick={cancel} style="outline" className="flex-1">
          Cancel
        </ButtonDesigned>
        <ButtonDesigned
          disableButton={!selectedMaterial || !clientName || getTotalAmount() === 0}
          type="button"
          onButtonClick={saveSalesCommitment}
          className="flex-1"
        >
          {isUpdate ? 'Save' : 'Add'}
        </ButtonDesigned>
      </div>

      {loading && <LoadingSpinnerOverlay size={64} />}
    </div>
  );
};
